import React from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { updateGroupName } from "../../services/groupService";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const GroupNameSchema = Yup.object().shape({
  name: Yup.string()
    .min(1, i18n.t("groupManagement.createModal.validation.nameLength"))
    .max(100, i18n.t("groupManagement.createModal.validation.nameLength"))
    .required(i18n.t("groupManagement.createModal.validation.nameRequired")),
});

const EditGroupNameModal = ({ open, onClose, group, onSuccess }) => {
  const classes = useStyles();

  const handleSubmit = async (values, actions) => {
    try {
      await updateGroupName(group.id, group.whatsappId, values.name);
      toast.success(i18n.t("groupManagement.toasts.nameUpdated"));
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toastError(err);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const initialValues = {
    name: group?.name || "",
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t("groupManagement.editNameModal.title")}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={GroupNameSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form>
            <DialogContent dividers>
              <div className={classes.root}>
                <Field
                  as={TextField}
                  label={i18n.t("groupManagement.editNameModal.form.name")}
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  placeholder={i18n.t(
                    "groupManagement.editNameModal.form.namePlaceholder"
                  )}
                  autoFocus
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
                {i18n.t("groupManagement.editNameModal.buttons.cancel")}
              </Button>
              <div className={classes.btnWrapper}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {i18n.t("groupManagement.editNameModal.buttons.save")}
                </Button>
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditGroupNameModal;
